import { useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, MapPin, Clock, Lock, Camera, Loader2, Upload, Sliders, ArrowLeft } from "lucide-react";
import type { PlayerProfile as PlayerProfileType, User as UserType } from "@shared/schema";
import { ClubPicker } from "@/components/ClubPicker";

const countryOptions = [
  { code: "DO", name: "República Dominicana" },
  { code: "PR", name: "Puerto Rico" },
  { code: "MX", name: "México" },
  { code: "CO", name: "Colombia" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "PE", name: "Perú" },
  { code: "VE", name: "Venezuela" },
  { code: "EC", name: "Ecuador" },
  { code: "PA", name: "Panamá" },
  { code: "CR", name: "Costa Rica" },
  { code: "GT", name: "Guatemala" },
  { code: "CU", name: "Cuba" },
  { code: "UY", name: "Uruguay" },
  { code: "PY", name: "Paraguay" },
  { code: "BO", name: "Bolivia" },
  { code: "HN", name: "Honduras" },
  { code: "SV", name: "El Salvador" },
  { code: "NI", name: "Nicaragua" },
  { code: "US", name: "Estados Unidos" },
  { code: "ES", name: "España" },
  { code: "BR", name: "Brasil" },
  { code: "OTHER", name: "Otro país" },
];

const playTimeOptions = [
  { id: "morning", label: "Mañana (6-12h)" },
  { id: "afternoon", label: "Tarde (12-18h)" },
  { id: "evening", label: "Noche (18-23h)" },
  { id: "weekend", label: "Fines de semana" },
];

const profileFormSchema = z.object({
  firstName: z.string().min(1, "El nombre es requerido"),
  lastName: z.string().min(1, "El apellido es requerido"),
  profileImageUrl: z.string().optional().or(z.literal("")),
  country: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  homeClubId: z.string().nullable().optional(),
  dateOfBirth: z.string().optional().or(z.literal("")),
  gender: z.enum(["male", "female", "other", "prefer_not_to_say"]).optional(),
  playingHand: z.enum(["right", "left", "ambidextrous"]).optional(),
  playerType: z.enum(["offensive", "defensive", "all_around"]).optional(),
  yearsPlaying: z.number().min(0).max(50).optional(),
  playingFrequency: z.enum(["once_week", "twice_week", "three_plus_week", "daily"]).optional(),
  preferredPlayTimes: z.array(z.string()).optional(),
});

type ProfileFormData = z.infer<typeof profileFormSchema>;

export function EditProfilePage() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const homeClubFieldRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const hasInitializedRef = useRef(false);
  const hasFocusedHomeClubRef = useRef(false);

  useEffect(() => {
    if (hasFocusedHomeClubRef.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("focus") !== "homeClub") return;
    if (!homeClubFieldRef.current) return;
    hasFocusedHomeClubRef.current = true;
    requestAnimationFrame(() => {
      homeClubFieldRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  });

  const { data: profile, isFetched: profileFetched } = useQuery<PlayerProfileType | null>({
    queryKey: ['/api/profile'],
    queryFn: async () => {
      const res = await fetch('/api/profile', { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data.profile || null;
    },
  });

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      profileImageUrl: user?.profileImageUrl || "",
      country: "",
      city: "",
      homeClubId: null,
      dateOfBirth: "",
      gender: undefined,
      playingHand: "right",
      playerType: "all_around",
      yearsPlaying: 0,
      playingFrequency: undefined,
      preferredPlayTimes: [],
    },
  });

  // Initialize form ONCE when BOTH the user and the profile query have settled.
  // Without waiting for the profile query, profile-backed fields (country,
  // etc.) could stay blank because the guard would lock initialization in.
  // Without the ref guard, re-running form.reset on every render would clobber
  // the user's typed input and could leave the submit state stuck.
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!user || !profileFetched) return;
    hasInitializedRef.current = true;
    form.reset({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      profileImageUrl: user.profileImageUrl || "",
      country: profile?.country || "",
      city: profile?.city || "",
      homeClubId: profile?.homeClubId || null,
      dateOfBirth: profile?.dateOfBirth || "",
      gender: profile?.gender || undefined,
      playingHand: profile?.playingHand || "right",
      playerType: profile?.playerType || "all_around",
      yearsPlaying: profile?.yearsPlaying || 0,
      playingFrequency: profile?.playingFrequency || undefined,
      preferredPlayTimes: profile?.preferredPlayTimes || [],
    });
  }, [profile, profileFetched, user, form.reset]);

  const updateUserMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; profileImageUrl?: string }) => {
      const response = await apiRequest('PATCH', '/api/auth/user', data);
      if (!response.ok) {
        throw new Error('Error updating user');
      }
      return response.json();
    },
    onError: (error) => {
      console.error('User update error:', error);
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: Partial<ProfileFormData>) => {
      const response = await apiRequest('PATCH', '/api/profile/player', data);
      if (!response.ok) {
        throw new Error('Error updating profile');
      }
      return response.json();
    },
    onError: (error) => {
      console.error('Profile update error:', error);
    },
  });

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch('/api/upload/profile-image', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }
      
      const result = await response.json();
      form.setValue('profileImageUrl', result.url);
      
      toast({
        title: "Foto actualizada",
        description: "Tu foto de perfil se ha subido correctamente.",
      });
      
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo subir la imagen. Intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Archivo muy grande",
          description: "El archivo debe ser menor a 5MB.",
          variant: "destructive",
        });
        return;
      }
      handleImageUpload(file);
    }
    event.target.value = '';
  };

  const onSubmit = async (data: ProfileFormData) => {
    try {
      // Update user basic info and player profile in parallel, but handle them separately
      await Promise.all([
        updateUserMutation.mutateAsync({
          firstName: data.firstName,
          lastName: data.lastName,
          profileImageUrl: data.profileImageUrl || undefined,
        }),
        updateProfileMutation.mutateAsync({
          country: data.country || undefined,
          city: data.city || undefined,
          homeClubId: data.homeClubId ?? null,
          dateOfBirth: data.dateOfBirth || undefined,
          gender: data.gender,
          playingHand: data.playingHand,
          playerType: data.playerType,
          yearsPlaying: data.yearsPlaying,
          playingFrequency: data.playingFrequency,
          preferredPlayTimes: data.preferredPlayTimes || [],
        }),
      ]);

      // Force refetch to ensure data is updated before navigating back
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['/api/auth/user'] }),
        queryClient.refetchQueries({ queryKey: ['/api/profile'] }),
      ]);

      toast({
        title: "Perfil actualizado",
        description: "Tus cambios han sido guardados correctamente.",
      });

      // Navigate back after toast is shown
      setTimeout(() => history.back(), 300);
    } catch (error: any) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: error?.message || "No se pudo actualizar el perfil. Intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return null;
  }

  const isSubmitting = updateUserMutation.isPending || updateProfileMutation.isPending;
  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-background">
      <header 
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Editar Perfil</h1>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 pt-3 safe-area-bottom">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="personal" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="personal" data-testid="tab-personal" className="gap-1">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Personal</span>
                </TabsTrigger>
                <TabsTrigger value="game" data-testid="tab-game" className="gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">Juego</span>
                </TabsTrigger>
                <TabsTrigger value="availability" data-testid="tab-availability" className="gap-1">
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline">Horarios</span>
                </TabsTrigger>
                <TabsTrigger value="preferences" data-testid="tab-preferences" className="gap-1">
                  <Sliders className="h-4 w-4" />
                  <span className="hidden sm:inline">Preferencias</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="personal" className="space-y-4 mt-4">
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className="relative">
                    <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                      <AvatarImage 
                        src={form.watch("profileImageUrl") || user.profileImageUrl || undefined} 
                        alt={user.firstName || "Avatar"} 
                        className="object-cover"
                      />
                      <AvatarFallback className="text-2xl font-bold bg-primary/10 text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="input-file-upload"
                  />
                  <input
                    type="file"
                    ref={cameraInputRef}
                    accept="image/*"
                    capture="user"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="input-camera-capture"
                  />
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full sm:w-auto"
                      data-testid="button-upload-photo"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Subir Foto
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cameraInputRef.current?.click()}
                      className="w-full sm:w-auto"
                      data-testid="button-take-photo"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Tomar Foto
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    JPG, PNG, WebP o GIF. Máximo 5MB.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre *</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-first-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Apellido *</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-last-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>País</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-country">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <ScrollArea className="h-60">
                            {countryOptions.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </ScrollArea>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ciudad</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Santo Domingo" data-testid="input-city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div ref={homeClubFieldRef} data-testid="field-home-club">
                  <FormField
                    control={form.control}
                    name="homeClubId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mi club</FormLabel>
                        <FormControl>
                          <ClubPicker
                            value={field.value || null}
                            onChange={(id) => field.onChange(id)}
                            country={form.watch("country") || null}
                            testId="profile-club-picker"
                          />
                        </FormControl>
                        <FormDescription>
                          Donde sueles jugar (opcional).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Nacimiento</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          type="date" 
                          data-testid="input-date-of-birth"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Género</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-gender">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="male">Masculino</SelectItem>
                          <SelectItem value="female">Femenino</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="p-4 bg-muted/50 rounded-lg border border-muted">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock className="h-4 w-4" />
                    <span>Nivel de Habilidad:</span>
                    <Badge variant="secondary" className="font-mono">
                      {profile?.skillLevel?.toFixed(1) || "0.0"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    El nivel de habilidad no puede modificarse después del registro inicial
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="game" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="yearsPlaying"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Años Jugando</FormLabel>
                      <FormControl>
                        <Input 
                          {...field}
                          type="number"
                          min={0}
                          max={50}
                          value={field.value || 0}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          data-testid="input-years-playing"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="playingHand"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mano Dominante</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-playing-hand">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="right">Diestro</SelectItem>
                          <SelectItem value="left">Zurdo</SelectItem>
                          <SelectItem value="ambidextrous">Ambidiestro</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="playerType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estilo de Juego</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-player-type">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="offensive">Ofensivo</SelectItem>
                          <SelectItem value="defensive">Defensivo</SelectItem>
                          <SelectItem value="all_around">Completo</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="availability" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="playingFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Frecuencia de Juego</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-frequency">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="once_week">1 vez por semana</SelectItem>
                          <SelectItem value="twice_week">2 veces por semana</SelectItem>
                          <SelectItem value="three_plus_week">3+ veces por semana</SelectItem>
                          <SelectItem value="daily">Diariamente</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="preferredPlayTimes"
                  render={() => (
                    <FormItem>
                      <FormLabel>Horarios Preferidos</FormLabel>
                      <div className="space-y-2">
                        {playTimeOptions.map((option) => (
                          <FormField
                            key={option.id}
                            control={form.control}
                            name="preferredPlayTimes"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(option.id)}
                                    onCheckedChange={(checked) => {
                                      const current = field.value || [];
                                      if (checked) {
                                        field.onChange([...current, option.id]);
                                      } else {
                                        field.onChange(current.filter((v) => v !== option.id));
                                      }
                                    }}
                                    data-testid={`checkbox-time-${option.id}`}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">
                                  {option.label}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="preferences" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-muted/50 border">
                    <p className="text-sm font-medium mb-2">Modo Oscuro</p>
                    <p className="text-xs text-muted-foreground mb-3">Cambia entre modo claro y oscuro</p>
                    <ThemeToggle />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => history.back()}
                className="w-full sm:w-auto"
                data-testid="button-cancel-edit"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full sm:w-auto"
                data-testid="button-save-profile"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </div>
  );
}
